import type {DynamicLabelData} from './types';

import fromPairs from 'lodash/fromPairs';
import mapValues from 'lodash/mapValues';
import {EditorState} from 'draft-js';


export const ENITITY_DYNAMIC_LABEL = 'DYNAMIC_LABEL';
export const ENITITY_PLACEHOLDER = 'PLACEHOLDER';

export const dynamicRegex: RegExp = /<+([^>]+)>+|\{\{([^}]+)\}\}/g;
export const escapedRegex: RegExp = /^<{2,}/g;

export const dynamicRegexParenthesis: RegExp = /\{\{(.*?)\}\}/g;

export const nestedDynamicRegexParenthesis: RegExp =
  /\{\{((?:[^{}]|\{\{[^{}]*\}\})*)\}\}/g;

export const escapedRegexParenthesis: RegExp = /^{{{3,}}/g;

export const MESSAGING_DYNAMIC_VARIABLES: ReadonlySet<string> = new Set([
  'scheduler_template_url',
  'secure_upload_link',
]);
/**
 * Parse a dynamic label string & get an object with its label & any extra attrs
 * @param  {string}            a dynamic-label-like string like
 *                               - '<foo>'
 *                               - '<foo|fallback: `bar`>',
 *                               - '<foo|extra:"bar"|fallback:"'baz'">'
 * @return {DynamicLabelData}  an object that tells us about the label with all
 *                                piped attributes in the `attrs` obj.
 */

export const dynamicLabelText = (match: string): DynamicLabelData => {
  const dynamicField = match.replace(/[<{>}]*/g, '').split('|');
  const [labelText, ...rest] = dynamicField;

  const pairs = rest.map((attr) =>
    attr.split(':').map((keyOrVal) => keyOrVal.trim()),
  );

  const quotedAttrs = fromPairs(pairs);
  // remove all the outer quote from the attribute value with slice
  const attrs = mapValues(quotedAttrs, (value) =>
    typeof value === 'string' ? value.slice(1, -1) : value,
  );

  return {
    label: labelText.trim(),
    attrs,
  };
};

/**
 * Scans `text` for dynamic-variable tokens (e.g. `<foo>`, `{{foo}}`) and returns
 * the label names of any tokens that aren't present in `labels`.
 */
const validationDynamicRegex: RegExp = /<+([^<>|]+)(\|[^<>]*)?>+/g;

export function findInvalidDynamicVariables(
  text: string,
  labels: ReadonlyArray<{name?: unknown; value?: unknown}> = [],
): string[] {
  const invalid = new Set<string>();
  let match;
  while ((match = validationDynamicRegex.exec(text)) !== null) {
    if (escapedRegex.exec(match[0]) !== null) {
      continue;
    }
    const {label} = dynamicLabelText(match[0]);
    if (!label) {
      continue;
    }
    if (MESSAGING_DYNAMIC_VARIABLES.has(label.toLowerCase())) {
      continue;
    }
    const found = labels.some(
      (l) =>
        (typeof l.name === 'string' &&
          l.name.toLowerCase() === label.toLowerCase()) ||
        (typeof l.value === 'string' &&
          l.value.toLowerCase() === label.toLowerCase()),
    );
    if (!found) {
      invalid.add(label);
    }
  }
  return Array.from(invalid);
}

/**
 * The function returns the range of given entity inside the block.
 * {
 *   anchorOffset: undefined,
 *   focusOffset: undefined,
 *   text: undefined,
 * }
 */
export function getEntityRange(
  editorState: EditorState,
  entityKey: string,
): Array<number> {
  const anchorKey = editorState.getSelection().getAnchorKey();
  const block = editorState.getCurrentContent().getBlockForKey(anchorKey);
  let entityRange = [0, 0];
  block.findEntityRanges(
    // @ts-expect-error - TS2339 - Property 'get' does not exist on type 'CharacterMetadata'.
    (value) => value.get('entity') === entityKey,
    (start, end) => (entityRange = [start, end]),
  );
  return entityRange;
}
